<template>
    <div
        class="input-range-component-two-knobbed"
        :class="classInputRangeTwoKnobbed"
    >
        <div
            v-if="min !== max"
            class="selected-range-indicator-container"
        >
            <div
                class="clamp-spacer left"
                :style="styleClampSpacerLeft"
            ></div>

            <div
                ref="indicatorElement"
                class="selected-range-indicator"
                @mousedown.prevent.stop="select($event as any)"
            >
                <div
                    class="clamp-spacer left"
                    :style="styleSelectedClampSpacerLeft"
                ></div>

                <div class="track"></div>

                <div class="range"></div>

                <div
                    class="clamp-spacer right"
                    :style="styleSelectedClampSpacerRight"
                ></div>
            </div>

            <div
                class="clamp-spacer right"
                :style="styleClampSpacerRight"
            ></div>
        </div>

        <InputRange
            :id="`${id}-0`"
            v-qa="'input-0'"
            v-qa.model="JSON.stringify(valueMin)"
            class="input-0"
            :class="getClassRangeInput(0)"
            :clamp
            :disabled
            :max="maxFinal"
            :min="minFinal"
            :model-value="valueMinUi"
            :step
            :value-formatter="(val: number) => valueFormatter(val, 0)"
            is-part-of-two-knobbed
            :hide-label="showUnifiedLabel"
            @change="onChangeMin"
            @update:model-value="updateSingleInputUiValue($event as number, 0)"
            @two-knobbed-position="sliderOnePos = $event"
        />

        <InputRange
            :id="`${id}-1`"
            v-qa="'input-1'"
            v-qa.model="JSON.stringify(valueMax)"
            class="input-1"
            :class="getClassRangeInput(1)"
            :clamp
            :disabled
            :max="maxFinal"
            :min="minFinal"
            :model-value="valueMaxUi"
            :step
            :value-formatter="(val: number) => valueFormatter(val, 1)"
            is-part-of-two-knobbed
            :hide-label="showUnifiedLabel"
            @change="onChangeMax"
            @update:model-value="updateSingleInputUiValue($event as number, 1)"
            @two-knobbed-position="sliderTwoPos = $event"
        />

        <Transition name="join-labels">
            <div
                v-if="showUnifiedLabel"
                ref="labelEl"
                class="label"
                :style="styleLabel"
            >
                <span class="half left"> {{ sliderLeft.label }} </span
                ><span class="dash">—</span
                ><span class="half right">
                    {{ sliderRight.label }}
                </span>
            </div>
        </Transition>
    </div>
</template>

<script lang="ts">
export type TwoKnobbedPosition = {
    progress: number
    left: number
    width: number
    label: string
    styleLabel: string
    clampPercentLeft: number
    clampPercentRight: number
    clampedToTotal: number
}
</script>

<script lang="ts" setup>
import { computed, ref, toRef, useTemplateRef } from 'vue'

import { useNextId } from '@/helpers/registry'
import useInputRange from './useInputRange'
import { useInputRangeLazy } from './useInputRangeLazy'
import { useInputRangeTwoKnobbedMouse } from './useInputRangeTwoKnobbedMouse'
import { useInputRangeTwoKnobbedStyle } from './useInputRangeTwoKnobbedStyle'
import { useInputRangeModels } from '@/components/ui/inputs/range/useInputRangeModel.ts'

import {
    defaults,
    type InputElementIndex,
    type InputRangeModel,
    type Props,
    type UpdateSingleInputUiValue,
} from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'

import InputRange from './InputRange.vue'
import { useElementBounding } from '@vueuse/core'

const props = withDefaults(defineProps<Props>(), defaults)

const emit = defineEmits<{
    'update:modelValue': [InputRangeModel]
}>()

const {
    minFinal,
    maxFinal,
    rangeClamped,
    styleClampSpacerLeft,
    styleClampSpacerRight,
} = useInputRange(props)

const id = `2knob-${useNextId()}`

const { min: valueMin, max: valueMax } = useInputRangeModels(
    () => props.modelValue,
    minFinal,
    maxFinal,
    (model: InputRangeModel) => {
        emit('update:modelValue', model)
    }
)

const { valueUi: valueMinUi, onChange: onChangeMin } = useInputRangeLazy(
    valueMin,
    true
)
const { valueUi: valueMaxUi, onChange: onChangeMax } = useInputRangeLazy(
    valueMax,
    true
)

const activeElement = ref<InputElementIndex>(0)

const updateSingleInputUiValue: UpdateSingleInputUiValue = (
    value: number,
    index?: InputElementIndex
) => {
    if (index !== undefined) {
        activeElement.value = index
    }

    switch (activeElement.value) {
        case 0:
            valueMinUi.value = value
            break
        case 1:
            valueMaxUi.value = value
            break
    }
}

const { styleSelectedClampSpacerLeft, styleSelectedClampSpacerRight } =
    useInputRangeTwoKnobbedStyle(minFinal, rangeClamped, valueMinUi, valueMaxUi)

const { indicatorElement, select } = useInputRangeTwoKnobbedMouse(
    valueMinUi,
    valueMaxUi,
    onChangeMin,
    onChangeMax,
    updateSingleInputUiValue,
    minFinal,
    toRef(() => props.step as number),
    rangeClamped,
    toRef(() => props.disabled)
)

const classInputRangeTwoKnobbed = computed(() => ({
    disabled: props.disabled,
}))

const getClassRangeInput = (index: InputElementIndex) => ({
    active: index === activeElement.value,
})

const sliderOnePos = ref<TwoKnobbedPosition>({
    left: 0,
    width: 0,
    progress: 0,
    label: '',
    styleLabel: '',
    clampPercentLeft: 0,
    clampPercentRight: 0,
    clampedToTotal: 0,
})

const sliderTwoPos = ref<TwoKnobbedPosition>({
    left: 0,
    width: 0,
    progress: 0,
    label: '',
    styleLabel: '',
    clampPercentLeft: 0,
    clampPercentRight: 0,
    clampedToTotal: 0,
})

const sliderLeft = computed(() =>
    sliderOnePos.value.progress < sliderTwoPos.value.progress
        ? sliderOnePos.value
        : sliderTwoPos.value
)

const sliderRight = computed(() =>
    sliderOnePos.value.progress > sliderTwoPos.value.progress
        ? sliderOnePos.value
        : sliderTwoPos.value
)
const showUnifiedLabel = computed(
    () =>
        sliderRight.value.left -
            (sliderLeft.value.left + sliderLeft.value.width) <
        2.6
)

const unifiedLabelEl = useTemplateRef('labelEl')
const { width: unifiedLabelWidth } = useElementBounding(unifiedLabelEl)

const styleLabel = computed(() => {
    return `
        left:
            clamp(
                -${sliderLeft.value.clampPercentLeft / sliderLeft.value.clampedToTotal}%,
                calc(
                    calc(
                        calc(${sliderLeft.value.styleLabel.replace('left: ', '')} + ${sliderRight.value.styleLabel.replace('left: ', '')})
                        / 2
                    )
                    - calc(${sliderLeft.value.width}px / 2) - 1.3px
                ),
                calc(${
                    100 +
                    sliderRight.value.clampPercentRight /
                        sliderRight.value.clampedToTotal
                }% - ${unifiedLabelWidth.value}px)
            )
    `.trim()
})
</script>

<style lang="scss" scoped>
.input-range-component-two-knobbed {
    position: relative;
    height: 70px;
    padding-top: 16px;

    &.disabled {
        cursor: not-allowed !important;

        .range {
            background-color: $grey-200 !important;
            cursor: not-allowed !important;
        }

        .track {
            background-color: $grey-200 !important;
            box-shadow: none !important;
            cursor: not-allowed !important;
        }
    }

    .input-0,
    .input-1 {
        position: absolute;
        width: 100%;
        pointer-events: none;
        z-index: 1;

        &.active {
            z-index: 2;
        }
    }

    .selected-range-indicator-container {
        position: absolute;
        top: 41px;
        width: 100%;
        height: 4px;
        display: flex;

        .clamp-spacer {
            pointer-events: none;

            &.left {
                flex: 0 0 0%;
            }

            &.right {
                flex: 0 0 0%;
            }
        }

        .selected-range-indicator {
            position: relative;
            flex: 1 1 auto;
            display: flex;
            margin-top: -4px;
            height: 12px;

            .track {
                position: absolute;
                margin-top: 4px;
                width: 100%;
                height: 4px;
                border-radius: 2px;
                background: $grey-200;
                box-shadow: inset 0 1px 1px rgb(0 0 0 / 10%);
                cursor: pointer;
            }

            .range {
                position: relative;
                margin-top: 4px;
                flex: 1 1 auto;
                height: 4px;
                background-color: $slate;
                pointer-events: none;
                cursor: pointer;
            }
        }
    }

    .min-max,
    .text-inputs {
        position: absolute;
        width: 100%;
        display: flex;
        justify-content: space-between;

        font-family: $effra-font-stack;
        font-weight: 400;
        font-size: 0.875rem;
        line-height: 0.875rem;
        text-align: center;
        letter-spacing: 0.05em;
        text-transform: uppercase;
    }

    .min-max {
        bottom: 68px;
    }

    .text-inputs {
        bottom: 10px;

        .text-input {
            width: 80px;
            height: 40px;
            border: 1px solid $grey-200;
            border-radius: 12px;
        }

        input[type='number'] {
            -moz-appearance: textfield;
            appearance: textfield;
            margin: 0;
        }
    }

    .label {
        position: absolute;
        font-family: $effra-font-stack;
        font-weight: 400;
        font-size: 0.875rem;
        line-height: 0.875rem;
        text-align: center;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        display: flex;

        white-space: nowrap;
        --label-border-radius: 4px;
        top: 0;
        color: $white;
        // background-color: $slate;
        // background-color: $blush;
        border-radius: var(--label-border-radius);

        z-index: 3;
        // opacity: 0.8;

        .dash {
            width: 14.7px;
            z-index: 4;
            display: flex;
            align-items: center;
        }

        .half {
            display: block;
            padding: 6px;

            position: relative;
            background-color: $slate;
            border-radius: var(--label-border-radius);

            z-index: 0;
        }

        .left::after,
        .right::before {
            content: '';
            background-color: $slate;
            // background-color: bisque;
            position: absolute;
            height: 100%;
            width: 15px;
            top: 0;
            z-index: -1;
        }

        --distance: -8px;
        --initial-border-radius: 50%;

        .left {
            padding-right: 0;

            &::after {
                right: var(--distance);
            }
        }

        .right {
            padding-left: 0;

            &::before {
                left: var(--distance);
            }
        }
    }

    .value-label {
        font-weight: 400;
        bottom: 0;

        &.min {
            left: 0;
        }

        &.max {
            right: 0;
        }
    }
}

.join-labels-enter-active {
    --duration: 0.15s;

    transition: border-radius var(--duration) ease;

    .dash {
        transition: opacity var(--duration) ease;
    }

    .half::before,
    .half::after {
        transition: border-radius var(--duration) ease;
    }

    .left::after {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }

    .right::before {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }
}

.join-labels-enter-from {
    .dash {
        opacity: 0;
    }

    .left::after {
        border-top-right-radius: var(--initial-border-radius);
        border-bottom-right-radius: var(--initial-border-radius);
    }
    .right::before {
        border-top-left-radius: var(--initial-border-radius);
        border-bottom-left-radius: var(--initial-border-radius);
    }
}
</style>
